import { ReCaptchaV3Provider } from "@firebase/app-check";
import {
  AppBar,
  type Authenticator,
  type CMSView,
  CircularProgressCenter,
  Drawer,
  FireCMS,
  FireCMSi18nProvider,
  ModeControllerProvider,
  NavigationRoutes,
  Scaffold,
  SideDialogs,
  SnackbarProvider,
  useBuildLocalConfigurationPersistence,
  useBuildModeController,
  useBuildNavigationController,
  useValidateAuthenticator,
} from "@firecms/core";
import {
  type FirebaseAuthController,
  FirebaseLoginView,
  type FirebaseSignInProvider,
  type FirebaseUserWrapper,
  useAppCheck,
  useFirebaseAuthController,
  useFirebaseStorageSource,
  useFirestoreDelegate,
  useInitialiseFirebase,
} from "@firecms/firebase";
import { CenteredView } from "@firecms/ui";
import { type ComponentProps, useCallback, useMemo } from "react";

import "@fontsource/rubik/index.css";
import "@fontsource/ibm-plex-mono/index.css";

import { authors } from "./collections/authors";
import { blogArticlesCollection } from "./collections/blogArticles";
import { content } from "./collections/content";
import { contentWeb } from "./collections/contentWeb";
import { audioCourses } from "./collections/courses/audioCourses";
import { introCourses } from "./collections/courses/introCourses";
import { onlineCoursesWeb } from "./collections/courses/onlineCoursesWeb";
import { developers } from "./collections/developers";
import { insuranceCheckFormData } from "./collections/insuranceCheckFormData";
import { meditationDailies } from "./collections/meditations/meditationDailies";
import { podcastEpisodesCollection } from "./collections/podcastEpisodes";
import { instagramLinks } from "./collections/instagramLinks";
import { firebaseConfig, isProdServer } from "./firebase-config";
import { cmsCustomViews } from "./views/cms-views";

export default function App() {
  const collections = useMemo(
    () => [
      audioCourses,
      introCourses,
      onlineCoursesWeb,
      meditationDailies,
      blogArticlesCollection,
      podcastEpisodesCollection,
      authors,
      developers,
      content,
      contentWeb,
      insuranceCheckFormData,
      instagramLinks,
    ],
    [],
  );

  const { firebaseApp, firebaseConfigLoading, configError } = useInitialiseFirebase({ firebaseConfig });

  const modeController = useBuildModeController();

  const signInOptions: FirebaseSignInProvider[] = ["google.com"];

  // FireCMS packages can end up duplicated in node_modules (core nested under firebase),
  // which makes their TypeScript types incompatible. Runtime is unaffected.
  const authController: FirebaseAuthController = useFirebaseAuthController({
    firebaseApp,
    signInOptions,
  }) as unknown as FirebaseAuthController;

  const userConfigPersistence = useBuildLocalConfigurationPersistence();

  const firestoreDelegate = useFirestoreDelegate({
    firebaseApp,
  }) as unknown;

  const storageSource = useFirebaseStorageSource({
    firebaseApp,
  }) as unknown;

  type ValidateAuthenticatorProps = Parameters<typeof useValidateAuthenticator>[0];
  type NavigationControllerProps = Parameters<typeof useBuildNavigationController>[0];
  type FireCMSProps = ComponentProps<typeof FireCMS>;

  const myAuthenticator: Authenticator<FirebaseUserWrapper> = useCallback(async ({ user }) => {
    const token = await user?.firebaseUser?.getIdTokenResult();
    const isAdmin = token?.claims.admin;
    if (!isAdmin) {
      throw new Error("Not allowed");
    }
    console.log("Allowing access to", user?.email);

    return true;
  }, []);

  const appCheckSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

  if (isProdServer && !appCheckSiteKey) {
    throw new Error("Missing VITE_RECAPTCHA_SITE_KEY for prod CMS deployment");
  }

  useAppCheck({
    firebaseApp,
    options:
      isProdServer && appCheckSiteKey
        ? {
            provider: new ReCaptchaV3Provider(appCheckSiteKey),
            isTokenAutoRefreshEnabled: true,
            debugToken: import.meta.env.VITE_APP_CHECK_DEBUG_TOKEN || undefined,
          }
        : undefined,
  });

  const { authLoading, canAccessMainView, notAllowedError } = useValidateAuthenticator({
    authController,
    authenticator: myAuthenticator,
    dataSourceDelegate: firestoreDelegate as unknown as ValidateAuthenticatorProps["dataSourceDelegate"],
    storageSource: storageSource as unknown as ValidateAuthenticatorProps["storageSource"],
  });

  const customViews: CMSView[] = useMemo(() => cmsCustomViews, []);

  const navigationController = useBuildNavigationController({
    disabled: authLoading,
    collections,
    views: customViews,
    authController,
    dataSourceDelegate: firestoreDelegate as unknown as NavigationControllerProps["dataSourceDelegate"],
  });

  if (firebaseConfigLoading || !firebaseApp) {
    return <CircularProgressCenter />;
  }

  if (configError) {
    return <CenteredView>{configError}</CenteredView>;
  }

  return (
    <FireCMSi18nProvider locale="de">
      <SnackbarProvider>
        <ModeControllerProvider value={modeController}>
          <FireCMS
            navigationController={navigationController}
            authController={authController}
            userConfigPersistence={userConfigPersistence}
            dataSourceDelegate={firestoreDelegate as unknown as FireCMSProps["dataSourceDelegate"]}
            storageSource={storageSource as unknown as FireCMSProps["storageSource"]}
          >
            {({ loading }) => {
              if (loading || authLoading) {
                return <CircularProgressCenter size="large" />;
              }

              if (!canAccessMainView) {
                return (
                  <FirebaseLoginView
                    authController={authController}
                    firebaseApp={firebaseApp}
                    signInOptions={signInOptions}
                    notAllowedError={notAllowedError}
                  />
                );
              }

              return (
                <Scaffold autoOpenDrawer={false}>
                  <AppBar title="Entspannungshelden CMS" />
                  <Drawer />
                  <NavigationRoutes />
                  <SideDialogs />
                </Scaffold>
              );
            }}
          </FireCMS>
        </ModeControllerProvider>
      </SnackbarProvider>
    </FireCMSi18nProvider>
  );
}
